/**
 * The cascade a notification follows while recipients stay silent.
 *
 * Mirrors `escalationPlan.ts` in coms, which is the source of truth: the
 * backend re-validates whatever is posted and the follow-up worker is what
 * actually climbs the ladder. Keep the limits below identical to the ones
 * there, so an operator is stopped by this form rather than by a rejected
 * request.
 */

import { NotificationType } from 'hooks/useNotification';

/**
 * Voice is deliberately absent. `ttsHome` and `ttsCell` appear in the channel
 * picker but nothing dispatches them, so offering them as a rung would build a
 * ladder whose last step silently does nothing.
 */
export const ESCALATION_CHANNEL_OPTIONS: {
  label: string;
  value: NotificationType;
}[] = [
  { label: 'PRATUS', value: NotificationType.IN_APP },
  { label: 'Email', value: NotificationType.EMAIL },
  { label: 'SMS', value: NotificationType.SMS },
  { label: 'Teams', value: NotificationType.TEAMS },
];

export const MAX_ESCALATION_STEPS = 5;
export const MAX_ESCALATION_WAIT_MINUTES = 10080;

export type EscalationStep = {
  /** Minutes to wait after the previous step, not after the original send. */
  waitMinutes: number;
  channels: NotificationType[];
  /** Try the recipient's next contact method rather than their primary. */
  walkContactMethod: boolean;
};

export type EscalationPlan = {
  steps: EscalationStep[];
};

export type EscalationPresetKey = 'none' | 'nudge' | 'ladder';

/**
 * Two named ladders cover the common cases. `none` is the default so escalation
 * is always something the sender opts into.
 */
export const ESCALATION_PRESETS: Record<
  EscalationPresetKey,
  EscalationPlan | null
> = {
  none: null,
  nudge: {
    steps: [
      {
        waitMinutes: 15,
        channels: [NotificationType.SMS],
        walkContactMethod: false,
      },
    ],
  },
  ladder: {
    steps: [
      {
        waitMinutes: 15,
        channels: [NotificationType.SMS],
        walkContactMethod: false,
      },
      {
        waitMinutes: 30,
        channels: [NotificationType.SMS, NotificationType.EMAIL],
        walkContactMethod: true,
      },
    ],
  },
};

export const ESCALATION_PRESET_CHOICES: {
  label: string;
  value: EscalationPresetKey;
}[] = [
  { label: 'No follow-up', value: 'none' },
  { label: 'Remind once', value: 'nudge' },
  { label: 'Remind, then try another device', value: 'ladder' },
];

export function emptyEscalationStep(): EscalationStep {
  return {
    waitMinutes: 15,
    channels: [NotificationType.SMS],
    walkContactMethod: false,
  };
}

export function addEscalationStep(plan: EscalationPlan): EscalationPlan {
  if (plan.steps.length >= MAX_ESCALATION_STEPS) return plan;
  return { steps: [...plan.steps, emptyEscalationStep()] };
}

export function removeEscalationStep(
  plan: EscalationPlan,
  index: number
): EscalationPlan {
  return { steps: plan.steps.filter((_, position) => position !== index) };
}

export function updateEscalationStep(
  plan: EscalationPlan,
  index: number,
  patch: Partial<EscalationStep>
): EscalationPlan {
  return {
    steps: plan.steps.map((step, position) =>
      position === index ? { ...step, ...patch } : step
    ),
  };
}

/**
 * Toggles one channel on a step, refusing to empty it.
 *
 * A step with no channels is rejected by the server, and silently dropping it
 * here would be worse than not letting the last one be unticked.
 */
export function toggleStepChannel(
  plan: EscalationPlan,
  index: number,
  channel: NotificationType
): EscalationPlan {
  const step = plan.steps[index];
  if (!step) return plan;

  const selected = step.channels.includes(channel);
  if (selected && step.channels.length === 1) return plan;

  return updateEscalationStep(plan, index, {
    channels: selected
      ? step.channels.filter((entry) => entry !== channel)
      : [...step.channels, channel],
  });
}

/** Why the server would reject this plan, in the order a sender should fix it. */
export function escalationPlanErrors(plan: EscalationPlan): string[] {
  const errors: string[] = [];

  if (plan.steps.length === 0) {
    errors.push('Add at least one follow-up step, or choose no follow-up.');
  }
  if (plan.steps.length > MAX_ESCALATION_STEPS) {
    errors.push(`A cascade can have at most ${MAX_ESCALATION_STEPS} steps.`);
  }
  plan.steps.forEach((step, index) => {
    if (!Number.isInteger(step.waitMinutes) || step.waitMinutes < 1) {
      errors.push(`Step ${index + 1} needs a wait of at least a minute.`);
    }
    if (step.waitMinutes > MAX_ESCALATION_WAIT_MINUTES) {
      errors.push(`Step ${index + 1} cannot wait longer than a week.`);
    }
    if (step.channels.length === 0) {
      errors.push(`Step ${index + 1} needs at least one channel.`);
    }
  });

  return errors;
}

function describeMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${minutes / 60} hr`;
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

function describeChannels(channels: NotificationType[]): string {
  const labels = channels.map(
    (channel) =>
      ESCALATION_CHANNEL_OPTIONS.find((option) => option.value === channel)
        ?.label ?? channel
  );
  if (labels.length <= 1) return labels[0] ?? '';

  const last = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(', ')} and ${last}`;
}

/**
 * One sentence per step, so the sender reads the ladder as a sequence of waits
 * rather than having to add the intervals up themselves.
 */
export function describeEscalationPlan(plan: EscalationPlan | null): string[] {
  if (!plan || plan.steps.length === 0) return [];

  return plan.steps.map((step, index) => {
    const device = step.walkContactMethod
      ? ' on their next contact method'
      : '';
    return `${index === 0 ? 'If no response after' : 'Then after another'} ${describeMinutes(
      step.waitMinutes
    )}, send via ${describeChannels(step.channels)}${device}, to whoever is still silent.`;
  });
}

/**
 * What to post.
 *
 * Null unless confirmation was requested, because a cascade advances on silence
 * and silence is indistinguishable from having no way to answer — the server
 * rejects the combination, so the form must not offer it.
 */
export function escalationPlanForSubmit(
  plan: EscalationPlan | null,
  acknowledgementRequired: boolean
): EscalationPlan | null {
  if (!acknowledgementRequired) return null;
  if (!plan || plan.steps.length === 0) return null;
  if (escalationPlanErrors(plan).length > 0) return null;
  return plan;
}

/** The preset a plan corresponds to, for showing the right control on reopen. */
export function escalationPresetKeyOf(
  plan: EscalationPlan | null
): EscalationPresetKey {
  if (!plan || plan.steps.length === 0) return 'none';

  const match = (Object.keys(ESCALATION_PRESETS) as EscalationPresetKey[]).find(
    (key) => JSON.stringify(ESCALATION_PRESETS[key]) === JSON.stringify(plan)
  );

  return match ?? 'none';
}
